// @ts-nocheck
import React, { useState } from 'react';

/**
 * A component that displays a list of buttons for selection
 * @param {Array} items - Array of items to display as buttons
 * @param {Function} onSelect - Function to call when an item is selected
 * @param {string} labelField - The property name of the label to display
 * @param {string} valueField - The property name of the value to return on selection
 * @param {string} categoryName - Name of the category being selected (for display)
 * @param {Function} onBack - Function to call when back button is clicked
 * @param {boolean} showBackButton - Whether to show the back button
 */
const ButtonSelectionList = ({ 
  items, 
  onSelect, 
  labelField = 'label', 
  valueField = 'value',
  categoryName = 'Items',
  onBack = null,
  showBackButton = false,
  disabledField = 'disabled'
}) => {
  const [searchValue, setSearchValue] = useState("");
  
  // SVG components inline
  const SearchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 21L15.0001 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="#A2BBBF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const XClose = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6L18 18" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  // Filter items based on search, only when categoryName is "Asset"
  const filteredItems = categoryName === "Asset"
    ? items.filter(item => 
        item[labelField].toLowerCase().includes(searchValue.toLowerCase()))
    : items;
  
  return (
    <div className="w-full">
      <div className="mb-3">
        <h3 className="text-gray-700 font-medium text-sm">Select {categoryName}</h3>
      </div>
      
      {/* Show search only for Asset selection */}
      {categoryName === "Asset" && (
        <div className="bg-white rounded-lg border border-gray-200 flex mb-4">
          <div className="">
            <div className="p-2">
              <div className="w-6 h-6 flex justify-center">
                <SearchIcon />
              </div>
            </div>
          </div>
          <input
            className="flex-1 rounded-r-lg pl-2 focus:outline-none"
            type="text"
            placeholder={`Search ${categoryName.toLowerCase()}`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <div className="">
            <div onClick={() => setSearchValue("")} className="p-2 cursor-pointer">
              <div className="w-6 h-6 flex justify-center">
                <XClose />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-2">
        {filteredItems.map((item, index) => (
          <button
            key={index}
            onClick={() => onSelect(item)}
            disabled={item[disabledField]}
            className={`text-left px-4 py-3 border rounded-md flex justify-between items-center transition-all duration-150 ${
              item[disabledField] 
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
                : 'border-gray-300 hover:border-eai-primary-accent hover:bg-opacity-10 hover:bg-eai-primary-accent hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-eai-primary-accent'
            }`}
          >
            <span>
              {/* For models, prioritize the model's label property if it exists */}
              {categoryName === "Model" ? (
                <span>
                  {/* Main model name in bold */}
                  <span className="font-medium">
                    {/* First try direct label property, then properties.label, then fallback to the computed label */}
                    {item.model?.label || 
                     (item.model?.properties && item.model.properties.label) || 
                     item[labelField] || "Unknown Model"}
                  </span>
                  
                  {/* Show model type as a descriptor line - styled to match asset button */}
                  <span className="text-xs text-gray-500 block mt-1">
                    {item.model?.modelType === "SENSITIVITY" ? "Sensitivity Model" : "Processed Model"}
                  </span>
                </span>
              ) : categoryName === "Asset" ? (
                // For assets, split the name and group into two lines
                <span>
                  {/* Asset name in bold - access directly from the asset object */}
                  <span className="font-medium block">
                    {item.asset?.name || "Unknown Asset"}
                  </span>
                  
                  {/* Asset group from the asset group map */}
                  <span className="text-xs text-gray-500 block mt-1">
                    {item.asset?.assetGroupId && item.groupNames ? 
                      item.groupNames[item.asset.assetGroupId]?.name || 'Unknown Group' : 
                      'Unknown Group'}
                  </span>
                </span>
              ) : (
                // For other categories, show the regular label field
                item[labelField]
              )}
            </span>
            {!item[disabledField] && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-eai-primary-accent" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {item[disabledField] && (
              <span className="px-2 py-1 text-xs bg-gray-200 text-gray-500 rounded">Coming Soon</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ButtonSelectionList;